import * as React from "react";
import { Check, Clock } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
interface PricingPlan {
  id: string;
  title: string;
  badge?: string;
  badgeVariant?: "default" | "secondary" | "outline";
  price: string;
  duration: string;
  savingNotice?: string;
  description?: string;
  inclusions?: string[];
  ctaLabel: string;
  ctaVariant: "default" | "secondary" | "outline";
  highlighted?: boolean;
}
const PRICING_PLANS: PricingPlan[] = [{
  id: "rmr",
  title: "Resting Metabolic Rate Assessment",
  price: "£90",
  duration: "around 30 minutes",
  inclusions: ["Pre-test preparation check", "Resting metabolic measurement", "Measured resting energy expenditure", "Results explanation", "Personalised results/report where supported by the CardioCoach system"],
  ctaLabel: "Book RMR Assessment",
  ctaVariant: "outline",
  highlighted: false
}, {
  id: "vo2max",
  title: "VO₂ Max & Aerobic Performance Assessment",
  badge: "MOST POPULAR FOR FITNESS & PERFORMANCE",
  badgeVariant: "secondary",
  price: "£160",
  duration: "around 45 minutes",
  inclusions: ["Pre-test health/readiness questionnaire", "Preparation and equipment setup", "Progressive exercise test", "VO₂ data & heart-rate response", "Personalised exercise zones", "Exercise calorie expenditure", "Exercise RER/substrate utilisation where available from the MAX", "Results explanation & CardioCoach results/report"],
  ctaLabel: "Book VO₂ Max Assessment",
  ctaVariant: "default",
  highlighted: false
}, {
  id: "complete",
  title: "Complete Metabolic & Aerobic Assessment",
  badge: "BEST VALUE / MOST COMPLETE",
  badgeVariant: "default",
  price: "£220",
  duration: "around 60–75 minutes",
  savingNotice: "RMR + VO₂ Max — save £30 compared with booking separately",
  description: "A comprehensive picture of energy use at rest and physiological response during progressively harder exercise.",
  inclusions: ["Includes both Resting Metabolic Rate Assessment and VO₂ Max & Aerobic Performance Assessment", "Full baseline resting energy expenditure breakdown", "Submaximal and peak aerobic threshold data", "Detailed physiological zone guidance & report"],
  ctaLabel: "Book Complete Assessment",
  ctaVariant: "default",
  highlighted: true
}, {
  id: "retest",
  title: "VO₂ Max Progress Retest",
  price: "£130",
  duration: "protocol-matched",
  description: "For existing PhysAnalytica VO₂ clients who want to repeat the same or closely matched protocol and objectively monitor change over time. Consistent preparation and test conditions are recommended for meaningful comparison.",
  ctaLabel: "Book a Retest",
  ctaVariant: "outline",
  highlighted: false
}];
export default function Vo2Pricing() {
  return <section id="vo2-pricing" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground">
            Choose your assessment
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 items-stretch">
          {PRICING_PLANS.map((plan, index) => <Card key={plan.id} data-index={index} className={`flex flex-col justify-between transition-all duration-200 ${plan.highlighted ? "border-primary shadow-md ring-2 ring-primary/20 bg-card relative xl:-translate-y-2" : "border-border shadow-xs hover:border-primary/40 bg-card"}`}>
              <div>
                <CardHeader className="space-y-3 pb-4">
                  {plan.badge ? <div className="min-h-6">
                      <Badge variant={plan.badgeVariant} className="text-[11px] font-semibold tracking-wide uppercase px-2.5 py-1">
                        {plan.badge}
                      </Badge>
                    </div> : <div className="min-h-6" aria-hidden="true" />}

                  <CardTitle className="text-xl font-bold leading-snug text-foreground min-h-[3rem]">
                    {plan.title}
                  </CardTitle>

                  <div className="pt-2">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground">
                        {plan.price}
                      </span>
                    </div>

                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-2">
                      <Clock className="w-3.5 h-3.5 shrink-0" />
                      <span>{plan.duration}</span>
                    </div>

                    {plan.savingNotice && <p className="text-xs font-medium text-primary mt-2">
                        {plan.savingNotice}
                      </p>}
                  </div>
                </CardHeader>

                <Separator className="mx-6 w-auto" />

                <CardContent className="pt-5 pb-6 text-sm">
                  {plan.description && <CardDescription className="text-xs text-muted-foreground mb-4 leading-relaxed">
                      {plan.description}
                    </CardDescription>}

                  {plan.inclusions && plan.inclusions.length > 0 && <div className="space-y-2.5">
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Includes:
                      </p>
                      <ul className="space-y-2">
                        {plan.inclusions.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-2 text-xs sm:text-sm text-foreground/90 leading-tight">
                            <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>)}
                      </ul>
                    </div>}
                </CardContent>
              </div>

              <CardFooter className="pt-2 pb-6">
                <Button asChild variant={plan.ctaVariant} className="w-full text-sm font-medium">
                  <Link to="#">{plan.ctaLabel}</Link>
                </Button>
              </CardFooter>
            </Card>)}
        </div>
      </div>
    </section>;
}